import { createFileRoute } from '@tanstack/react-router'
import { TiArrowLeftThick } from 'react-icons/ti'

export const Route = createFileRoute('/coffee-pot')({
  component: CoffeePot,
})

function CoffeePot() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="mb-6">
          <a href="/" className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
            <TiArrowLeftThick /> Home
          </a>
        </div>
        
        <h2 className="text-3xl font-bold text-gray-900 mb-6">WiFi Coffee Pot</h2>
        
        <div className="prose max-w-none">
          <p className="mb-4">On Youtube (writeup below):</p>
          
          <div className="mb-6">
            <iframe 
              width="560" 
              height="315" 
              src="https://www.youtube.com/embed/IZ4snVpUOLM" 
              title="YouTube video player"
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="max-w-full"
            />
          </div>

          <p className="mb-4">On GitHub:</p>

          <div className="mb-6">
            <a href="https://github.com/jchilton/FreshPots">
              <img src="https://gh-card.dev/repos/jchilton/FreshPots.svg" alt="FreshPots GitHub Repository" />
            </a>
          </div>

          <p className="mb-4">
            The first clip was recorded on 7/3/2022 and the last on 8/23, so the whole project took about seven weeks.
            Let's talk about it.
          </p>
          
          <p className="mb-4">
            <strong>Phase 1:</strong> pre-contemplation. I joke about this idea, not really imagining that I will actually do it.
          </p>
          
          <p className="mb-4">
            <strong>Phase 2:</strong> I record the first clip, not sure if this is going to work.
          </p>
          
          <p className="mb-4">
            <strong>Phase 3:</strong> pen and paper sketches of app UI, opening the coffee pot to see if I can fit the hardware in there
            (I decided it would be possible, but too tight for service in case anything broke, and taking it apart would be a
            nightmare), and making a list of parts: right off the bat, microcontroller, relay, breadboard, soldering iron,
            jumper wires, resistors, LED's. At this phase I also did some Android training since I had never used Kotlin
            and hadn't used relatively current Android API's.
          </p>
          
          <p className="mb-4">
            <strong>Phase 4:</strong> grind loop. The first batch of hardware arrived. Soon after, the{' '}
            <a href="https://store-usa.arduino.cc/products/arduino-nano-33-iot" className="text-blue-600 hover:underline">
              Arduino Nano 33 IoT
            </a>{' '}
            sold out and has been since. I kept going, like a guy on a collapsing bridge. In the video, you see me discuss 
            the initial use of the system to light an LED. This really was a live test of a huge amount of code. Usually 
            experts recommend testing little bits at a time (write only as much as you can test) so you don't get too many 
            bugs at once (the difficulty to fix a system of related bugs, or bad logic, is much more than simple ones.), 
            but I have done this stuff before, and this <em>fun project</em> does not call for the discipline of professional work.
          </p>
          
          <p className="mb-4">
            One thing I don't clearly say in the video is that for the purpose of the test, the LED is being used as a
            stand-in for the actual coffee pot. Instead of switching to complete a DC circuit with resistors and an LED, the
            relay would switch the AC wall current. The LED is just a simple, visual, sort of traditional breadboard equivalent
            of computer programming's "Hello, World!". The plan changed a few times, so I bounced back and
            forth between programming and buying parts. I would build some, then get stuck because I determined what parts I
            wanted to use, order them online, then wait. I would usually order things at the end of my weekend, for them to get
            here Wed-Sat, in time for me to do another sprint on the weekend, fitting the software in in-between.
          </p>
          
          <p className="mb-4">
            <strong>The hardware side of things:</strong> the build, which is put into time-lapse, took place over the course of probably
            3 or 4 weekends. I brutally tore open a wall wart AC to DC adapter which was not meant to be opened, and got a 5v
            power supply. I arranged the parts on the desk and carefully measured their 3 spatial dimensions, then included some
            wiggle room. I used these to shop for a project box. The one I found had a gasket. When I selected the relay, I had
            to find one that met two constraints: it had to be able to switch the wall's AC current at a certain amperage
            rating, and it had to run at a very low voltage; my Arduino Nano's GPIO supplies 3.3v. The coffee pot says it
            uses 700W, so since P=IV, 700=115A, A= about 6. So the coffee pot pulls about 6 amps. I found a relay fitting these
            parameters.
          </p>
          
          <p className="mb-4">
            The first step was to mount the hardware on to the project box. I used a printer power cable for the wall to the
            box, so I bought a{' '}
            <a href="https://www.digikey.com/en/products/detail/schurter-inc./4300.0105/8299836" className="text-blue-600 hover:underline">
              socket such as this one from Digi-Key
            </a>
            . I was going to use a polarized on on the other end, but instead decided to use a{' '}
            <a href="https://www.jameco.com/z/161-0715-Kobiconn-Power-Entry-Connector-NEMA-1-15P-2-Prong-Receptacle-Panel-Mount-Snap-In_2321239.html" className="text-blue-600 hover:underline">
              2-prong
            </a>{' '}
            wall socket, so that the coffee pot would be unmodified and I could plug it back into the wall if anything went
            wrong with my box. I used{' '}
            <a href="https://www.harborfreight.com/8v-cordless-variable-speed-rotary-tool-kit-40-piece-58162.html" className="text-blue-600 hover:underline">
              Harbor Freight's just-released cordless rotary tool
            </a>{' '}
            which was so new I had to ask for it, and the clerk got their only one out from unshelved stock, noting that he was 
            pretty jealous. I used the digital caliper and scribe to mark out symmetrical cut-out marks for the power entry 
            connectors, Dremel-ed holes for them, and super-glued them in. On the side, I drilled holes for the LED holders.
          </p>
          
          <p className="mb-4">
            I needed resistors to protect the LED's, and I used V=IR, 3.3v=.015R (assuming these LED's draw
            15mA) to find my resistor value. I clipped the + lead (the longer one) on the LED's and soldered the
            also-trimmed resistors to them, then insulated them. With the box ready, I started pulling wire and
            pre-wiring the setup outside of the box. This included crimping spade connectors, soldering wires to the
            four leads of the AC/DC adapter, and tinning and fastening some wires into screw terminals. That reminds me,
            I used a{' '}
            <a href="https://a.co/d/fNR1e2S" className="text-blue-600 hover:underline">
              screw terminal adapter for the Nano
            </a>{' '}
            for security and modifiability. When all the connections were made, soldered, crimped, and screwed in, it was time to hot
            glue the components to the inside walls of the box. I had been adjusting where I planned to put everything
            as I installed the power ports and LED's and it was a tight fit. I had not realized how bulky
            insulated spades are, and the Nano adapter was unplanned-for. In the end, it did all fit, but just barely. I
            could have taken some spades off and used butt connectors or solder in the worst case, but I didn't
            have to.
          </p>
          
          <p className="mb-4">
            <strong>The software side of things:</strong> Making things talk is notoriously difficult, and I make it look easy because I
            only choose really simple projects. Actually no, you have to know about threading, timing, protocols, the{' '}
            <a href="https://en.wikipedia.org/wiki/State_pattern" className="text-blue-600 hover:underline">
              state pattern
            </a>
            , binary and bitwise operations, character encodings, endianness, message framing, and such a "monstrous heap of nonsense, 
            a hodgepodge of foolishness" (poorly remembered quotes from a translation of <em>Don Quixote</em>, describing his windmill
            antics) that upon getenv:
            ... and that's just for a simple project like this. The{' '}
            <a href="https://github.com/jchilton/FreshPots/blob/main/Arduino/mdns_1/mdns_1.ino#L98" className="text-blue-600 hover:underline">
              protocol itself is documented in the comment linked here
            </a>.
          </p>
          
          <p className="mb-4">
            Thanks very much to{' '}
            <a href="https://github.com/contrem/" className="text-blue-600 hover:underline">Michael Contreras</a>
            {' '}for the{' '}
            <a href="https://github.com/contrem/arduino-timer" className="text-blue-600 hover:underline">Arduino Timer</a>
            {' '}library. This library is exactly what you want it to be, with a nice API and no spillage. The only thing I could 
            have asked for would be a cancel(id) instead of cancelling all timers. That had to be worked around, but it was not too bad.
          </p>
          
          <p className="mb-4">
            Android app development was mostly new to me, and Kotlin was totally. Luckily, since I know the fundamentals of
            application development, I could drag and drop code samples to get this outcome, more or less. It's amazing how
            little actual physical work it takes to get this result—it goes to the glory of the Android team, and some
            credit to myself, for all the years spent learning how to do this. Android actually requires you to run UI tasks and
            IO tasks in their own threads. It's great.
          </p>
          
          <p className="mb-4">
            I thought I was really cute and funny taking photos of the coffee pot for status images:
          </p>
          
          <div className="mb-4">
            <img src="/coffee-pot-icons.png" alt="Coffee pot status icons" className="max-w-full" />
          </div>
          
          <p className="mb-4">
            I made no effort to finish documentation beyond necessary to clarify my own thoughts; I made no tests; I made no
            effort to refactor the code from shortest-path job completion to a refined, well-structured professional-quality
            product because this isn't that, it's just my daily-drinker coffee pot.
          </p>
          
          <p className="mb-4">
            Please note: products I linked to are not necessarily the ones I used, but are close enough.
          </p>
        </div>
      </div>
    </div>
  )
}
